import {
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from "@angular/core";
import { Router } from "@angular/router";
import { MainState } from "@app/store/state";
import { UserInterface } from "@core/interfaces/user.interface";
import { AuthService } from "@core/services/auth.service";
import { DeviceService } from "@core/services/device.service";
import { EquipmentItemDisplayProperty, EquipmentItemService } from "@core/services/equipment-item.service";
import { LoadingService } from "@core/services/loading.service";
import { UserSubscriptionService } from "@core/services/user-subscription/user-subscription.service";
import { distinctUntilKeyChangedOrNull, UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { SimplifiedSubscriptionName } from "@core/types/subscription-name.type";
import { LoadUser } from "@features/account/store/auth.actions";
import { selectUser } from "@features/account/store/auth.selectors";
import { AccessoryService } from "@features/equipment/services/accessory.service";
import { CameraDisplayProperty, CameraService } from "@features/equipment/services/camera.service";
import { EquipmentApiService } from "@features/equipment/services/equipment-api.service";
import { FilterService } from "@features/equipment/services/filter.service";
import { MountService } from "@features/equipment/services/mount.service";
import { SensorDisplayProperty, SensorService } from "@features/equipment/services/sensor.service";
import { TelescopeService } from "@features/equipment/services/telescope.service";
import {
  GetMostOftenUsedWith,
  LoadBrand,
  LoadEquipmentItem,
  LoadSensor
} from "@features/equipment/store/equipment.actions";
import {
  selectBrand,
  selectEquipmentItem,
  selectMostOftenUsedWithForItem
} from "@features/equipment/store/equipment.selectors";
import { BrandInterface } from "@features/equipment/types/brand.interface";
import { instanceOfCamera } from "@features/equipment/types/camera.interface";
import {
  EquipmentItemReviewerDecision,
  EquipmentItemType
} from "@features/equipment/types/equipment-item-base.interface";
import { EquipmentItem } from "@features/equipment/types/equipment-item.type";
import { instanceOfSensor } from "@features/equipment/types/sensor.interface";
import { NgbModal, NgbModalRef, NgbOffcanvas } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { AssignItemModalComponent } from "@shared/components/equipment/summaries/assign-item-modal/assign-item-modal.component";
import { MostOftenUsedWithModalComponent } from "@shared/components/equipment/summaries/item/summary/most-often-used-with-modal/most-often-used-with-modal.component";
import { SubscriptionRequiredModalComponent } from "@shared/components/misc/subscription-required-modal/subscription-required-modal.component";
import { Observable } from "rxjs";
import { filter, map, take, takeWhile, tap } from "rxjs/operators";

interface EquipmentItemProperty {
  name: string;
  value: Observable<string | number>;
  link?: string;
  show?: Observable<boolean>;
}

@Component({
  selector: "astrobin-equipment-item-summary",
  templateUrl: "./item-summary.component.html",
  styleUrls: ["./item-summary.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ItemSummaryComponent extends BaseComponentDirective implements OnChanges {
  readonly UtilsService = UtilsService;
  readonly EquipmentItemDisplayProperty = EquipmentItemDisplayProperty;
  readonly SHOW_MAX_RELATED_ITEMS = 2;
  @Input() item: EquipmentItem;
  @Input() showName = true;
  @Input() showImage = true;
  @Input() showLargeImage = false;
  @Input() showProperties = true;
  @Input() showEmptyProperties = false;
  @Input() showClass = true;
  @Input() showSubItem = true;
  @Input() showMeta = false;
  @Input() showStats = true;
  @Input() showViewLink = false;
  @Input() enableBrandLink = false;
  @Input() showCommunityNotes = false;
  @Input() showMostOftenUsedWith = false;
  @Input() showEditButtons = true;
  @Input() showDataDoesNotUpdateInRealTime = true;
  @Input() striped = true;
  @Input() bordered = false;
  @Output() editButtonClick = new EventEmitter<EquipmentItem>();
  image: string;
  placeholder: string;
  subItemLabel: string;
  relatedItemsLabel: string;
  moreRelatedItemsLabel: string;
  createdBy$: Observable<UserInterface>;
  assignee$: Observable<UserInterface | null>;
  reviewedBy$: Observable<UserInterface>;
  lastUpdateVisible: boolean;

  @ViewChild("moreRelatedItemsOffcanvas")
  protected moreRelatedItemsOffcanvas: TemplateRef<any>;

  protected brand: BrandInterface;
  protected subItem: EquipmentItem;
  protected relatedItems: EquipmentItem[];
  protected subItemCollapsed = true;
  protected properties: EquipmentItemProperty[];
  protected mostOftenUsedWith$: Observable<{ item$: Observable<EquipmentItem>; matches: number }[]>;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly equipmentApiService: EquipmentApiService,
    public readonly translateService: TranslateService,
    public readonly utilsService: UtilsService,
    public readonly equipmentItemService: EquipmentItemService,
    public readonly cameraService: CameraService,
    public readonly telescopeService: TelescopeService,
    public readonly sensorService: SensorService,
    public readonly mountService: MountService,
    public readonly filterService: FilterService,
    public readonly accessoryService: AccessoryService,
    public readonly modalService: NgbModal,
    public readonly offcanvasService: NgbOffcanvas,
    public readonly userSubscriptionService: UserSubscriptionService,
    public readonly windowRefService: WindowRefService,
    public readonly authService: AuthService,
    public readonly loadingService: LoadingService,
    public readonly changeDetectorRef: ChangeDetectorRef,
    public readonly deviceService: DeviceService,
    public readonly router: Router
  ) {
    super(store$);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.item) {
      return;
    }

    // Compute placeholder and image
    const type = this.equipmentItemService.getType(this.item);
    this.placeholder = `/assets/images/${EquipmentItemType[type].toLowerCase()}-placeholder.png?v=2`;
    this.image = (this.item.thumbnail as string) || (this.item.image as string) || this.placeholder;

    // Compute sub-item and related items labels
    if (this.item.klass === EquipmentItemType.CAMERA) {
      this.subItemLabel = this.cameraService.getPrintablePropertyName(CameraDisplayProperty.SENSOR, true);
    } else if (this.item.klass === EquipmentItemType.SENSOR) {
      this.subItemLabel = this.sensorService.getPrintablePropertyName(SensorDisplayProperty.CAMERAS, true);
    } else {
      this.subItemLabel = this.translateService.instant("Sub-item");
    }
    this.relatedItemsLabel = instanceOfSensor(this.item)
      ? this.sensorService.getPrintablePropertyName(SensorDisplayProperty.CAMERAS, true)
      : this.translateService.instant("Related items");

    // Compute user-related observables
    if (this.item.createdBy) {
      this.createdBy$ = this.store$.select(selectUser, this.item.createdBy).pipe(distinctUntilKeyChangedOrNull("id"));
      this.store$.dispatch(new LoadUser({ id: this.item.createdBy }));
    }
    if (this.item.assignee) {
      this.assignee$ = this.store$.select(selectUser, this.item.assignee).pipe(distinctUntilKeyChangedOrNull("id"));
      this.store$.dispatch(new LoadUser({ id: this.item.assignee }));
    }
    if (this.item.reviewedBy) {
      this.reviewedBy$ = this.store$.select(selectUser, this.item.reviewedBy).pipe(distinctUntilKeyChangedOrNull("id"));
      this.store$.dispatch(new LoadUser({ id: this.item.reviewedBy }));
    }

    // Compute last update visibility
    if (!this.item.updated) {
      this.lastUpdateVisible = false;
    } else {
      const created = new Date(this.item.created).getTime();
      const updated = new Date(this.item.updated).getTime();
      this.lastUpdateVisible = updated - created >= 60000;
    }

    if (!this.item.createdBy) {
      this.showMeta = false;
    }

    if (this.item.brand) {
      this.store$
        .select(selectBrand, this.item.brand)
        .pipe(
          filter(brand => !!brand),
          takeWhile(brand => !this.brand || this.brand.id !== brand.id)
        )
        .subscribe(brand => {
          this.brand = brand;
          this.changeDetectorRef.markForCheck();
        });
    }

    if (instanceOfCamera(this.item) && this.item.sensor) {
      this.store$.dispatch(new LoadSensor({ id: this.item.sensor }));
      this.store$
        .select(selectEquipmentItem, { id: this.item.sensor, type: EquipmentItemType.SENSOR })
        .pipe(
          filter(sensor => !!sensor),
          take(1),
          tap(sensor => {
            if (sensor.brand) {
              this.store$.dispatch(new LoadBrand({ id: sensor.brand }));
            }
          })
        )
        .subscribe(sensor => {
          this.subItem = sensor;
          this.changeDetectorRef.markForCheck();
        });
    }

    if (instanceOfSensor(this.item) && this.item.cameras) {
      this.relatedItems = [];
      this.item.cameras.forEach(cameraId => {
        this.store$.dispatch(new LoadEquipmentItem({ id: cameraId, type: EquipmentItemType.CAMERA }));
        this.store$
          .select(selectEquipmentItem, { id: cameraId, type: EquipmentItemType.CAMERA })
          .pipe(
            filter(camera => !!camera),
            take(1),
            tap(camera => {
              if (camera.brand) {
                this.store$.dispatch(new LoadBrand({ id: camera.brand }));
              }
            })
          )
          .subscribe(camera => {
            this.relatedItems.push(camera);
            this.moreRelatedItemsLabel =
              this.relatedItems.length > this.SHOW_MAX_RELATED_ITEMS
                ? this.translateService.instant("+ {{ count }} more", {
                    count: this.relatedItems.length - this.SHOW_MAX_RELATED_ITEMS
                  })
                : "";
            this.changeDetectorRef.markForCheck();
          });
      });
    }

    if (this.item.reviewerDecision === EquipmentItemReviewerDecision.APPROVED && this.showMostOftenUsedWith) {
      const payload = { itemType: this.item.klass, itemId: this.item.id, allowDIY: true };
      this.store$.dispatch(new GetMostOftenUsedWith(payload));
      this.mostOftenUsedWith$ = this.store$.select(selectMostOftenUsedWithForItem, payload).pipe(
        filter(data => !!data),
        take(1),
        map(data => {
          return Object.keys(data)
            .map(key => {
              const klass: EquipmentItemType = EquipmentItemType[key.split("-")[0]];
              const id: EquipmentItem["id"] = parseInt(key.split("-")[1], 10);
              const itemPayload = { type: klass, id };
              this.store$.dispatch(new LoadEquipmentItem(itemPayload));
              return {
                item$: this.store$.select(selectEquipmentItem, itemPayload).pipe(
                  filter(item => !!item),
                  take(1)
                ),
                matches: parseInt(data[key], 10)
              };
            })
            .sort((a, b) => b.matches - a.matches);
        })
      );
    }

    this.computeProperties$()
      .pipe(take(1))
      .subscribe(properties => {
        this.properties = properties.filter(p => p !== null);
        this.changeDetectorRef.markForCheck();
      });
  }

  assign() {
    const modalRef = this.modalService.open(AssignItemModalComponent);
    const componentInstance: AssignItemModalComponent = modalRef.componentInstance;
    componentInstance.item = this.item;
    modalRef.closed.subscribe((item: EquipmentItem) => {
      this.item = item;
      this.changeDetectorRef.markForCheck();
      if (item.assignee) {
        this.store$.dispatch(new LoadUser({ id: item.assignee }));
      }
    });
  }

  itemTypeSupportsMostOftenUsedWith(): boolean {
    return (
      this.item.reviewerDecision === EquipmentItemReviewerDecision.APPROVED &&
      [
        EquipmentItemType.CAMERA,
        EquipmentItemType.TELESCOPE,
        EquipmentItemType.MOUNT,
        EquipmentItemType.FILTER
      ].indexOf(this.item.klass) > -1
    );
  }

  viewMoreRelatedItems() {
    this.offcanvasService.open(this.moreRelatedItemsOffcanvas, {
      position: this.deviceService.offcanvasPosition(),
      panelClass: "offcanvas-more-related-items"
    });
  }

  viewMoreMostOftenUsedWith() {
    this.currentUser$.pipe(take(1)).subscribe(user => {
      if (!user) {
        void this.router.navigate(["/account/login"]);
        return;
      }
      this.userSubscriptionService.fullSearchAllowed$().subscribe(allowed => {
        if (allowed) {
          const modalRef: NgbModalRef = this.modalService.open(MostOftenUsedWithModalComponent);
          const componentInstance: MostOftenUsedWithModalComponent = modalRef.componentInstance;
          componentInstance.item = this.item;
        } else {
          const modalRef: NgbModalRef = this.modalService.open(SubscriptionRequiredModalComponent);
          const componentInstance: SubscriptionRequiredModalComponent = modalRef.componentInstance;
          componentInstance.minimumSubscription = SimplifiedSubscriptionName.ASTROBIN_ULTIMATE_2020;
        }
        this.changeDetectorRef.markForCheck();
      });
    });
  }

  private computeProperties$(): Observable<EquipmentItemProperty[]> {
    return this.equipmentItemService.computeProperties$(this.item, this.showClass, this.showEmptyProperties);
  }
}
